<template>
  <div class="agent-dashboard">
    <!-- 顶部概览头部 -->
    <div class="dashboard-header">
      <div class="header-left">
        <h1 class="page-title">代理工作台</h1>
        <p class="welcome-text">欢迎回来，{{ agentInfo.name }}</p>
        <div class="level-badge">
          <span class="badge">{{ agentInfo.level }}</span>
          <span class="level-text">代理</span>
        </div>
      </div>
      <div class="header-right">
        <div class="current-date">
          <span class="date">{{ currentDate }}</span>
          <span class="weekday">{{ currentWeekday }}</span>
        </div>
        <div class="header-actions">
          <el-button type="primary" icon="el-icon-plus" @click="quickAddLead">快速添加线索</el-button>
          <el-button icon="el-icon-refresh" @click="refreshData">刷新数据</el-button>
        </div>
      </div>
    </div>

    <!-- 数据卡片区域 -->
    <div class="stats-cards">
      <div class="row">
        <div class="col" v-for="stat in statsData" :key="stat.title">
          <div class="stat-card" :class="stat.type">
            <div class="stat-icon">
              <i :class="stat.icon"></i>
            </div>
            <div class="stat-content">
              <div class="stat-title">{{ stat.title }}</div>
              <div class="stat-value">{{ stat.value }}</div>
              <div class="stat-trend" :class="stat.trend.type">
                <i :class="stat.trend.icon"></i>
                <span>{{ stat.trend.value }}</span>
                <span class="trend-text">较昨日</span>
              </div>
            </div>
            <div class="stat-action" @click="handleStatClick(stat.type)">
              查看详情 <i class="el-icon-right"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="dashboard-content">
      <!-- 左侧 -->
      <div class="content-left">
        <!-- 业绩趋势图 -->
        <div class="chart-section">
          <div class="section-header">
            <h3 class="section-title">业绩趋势</h3>
            <div class="time-filter">
              <el-radio-group v-model="trendPeriod" size="small" @change="updateTrendChart">
                <el-radio-button label="week">本周</el-radio-button>
                <el-radio-button label="month">本月</el-radio-button>
                <el-radio-button label="quarter">本季</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <div class="echarts-container">
            <v-chart
                ref="chart"
                class="performance-chart"
                :option="chartOption"
                :autoresize="true"
                @mouseover="onChartMouseOver"
                @mouseout="onChartMouseOut"
            />
          </div>
        </div>

        <!-- 待办事项 + 最近动态 -->
        <div class="todo-activities-wrapper">
          <div class="todo-section">
            <div class="section-header">
              <h3 class="section-title">待办事项</h3>
              <span class="todo-count">{{ todos.length }} 项</span>
            </div>
            <div class="todo-list">
              <div v-for="todo in todos" :key="todo.id" class="todo-item">
                <div class="todo-checkbox">
                  <el-checkbox v-model="todo.completed" @change="toggleTodo(todo.id)"></el-checkbox>
                </div>
                <div class="todo-content">
                  <div class="todo-title" :class="{ completed: todo.completed }">
                    {{ todo.title }}
                  </div>
                  <div class="todo-meta">
                    <span class="todo-time">{{ todo.time }}</span>
                    <span class="todo-priority" :class="todo.priority">{{ todo.priorityLabel }}</span>
                  </div>
                </div>
                <div class="todo-actions">
                  <el-button type="text" @click="handleTodoAction(todo)">处理</el-button>
                </div>
              </div>
            </div>
            <div class="todo-footer">
              <el-button type="text" @click="viewAllTodos">查看全部</el-button>
              <el-button type="text" @click="addTodo">添加待办</el-button>
            </div>
          </div>

          <div class="activities-section">
            <div class="section-header">
              <h3 class="section-title">最近动态</h3>
              <el-button type="text" @click="refreshActivities">刷新</el-button>
            </div>
            <div class="activities-list">
              <div v-for="activity in recentActivities" :key="activity.id" class="activity-item">
                <div class="activity-icon" :class="activity.type">
                  <i :class="activity.icon"></i>
                </div>
                <div class="activity-content">
                  <div class="activity-title">{{ activity.title }}</div>
                  <div class="activity-desc">{{ activity.description }}</div>
                  <div class="activity-time">{{ activity.time }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧 -->
      <div class="content-right">
        <!-- 快捷操作 -->
        <div class="quick-actions-section">
          <h3 class="section-title">快捷操作</h3>
          <div class="actions-grid">
            <div
                v-for="action in quickActions"
                :key="action.title"
                class="action-item"
                @click="handleQuickAction(action.action)"
            >
              <div class="action-icon" :style="{ backgroundColor: action.color }">
                <i :class="action.icon"></i>
              </div>
              <div class="action-content">
                <div class="action-title">{{ action.title }}</div>
                <div class="action-desc">{{ action.desc }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 资金账户（从后端接口查询） -->
        <div class="funds-section" v-loading="fundsLoading">
          <div class="section-header">
            <h3 class="section-title">资金账户</h3>
            <el-button type="primary" size="small" @click="openWithdrawDialog">
              提现 <i class="el-icon-right"></i>
            </el-button>
          </div>
          <div class="funds-content">
            <div class="funds-item">
              <span class="funds-label">累计获得</span>
              <span class="funds-value">¥{{ formatCurrency(agentFunds.totalProfit) }}</span>
            </div>
            <div class="funds-item">
              <span class="funds-label">已提现</span>
              <span class="funds-value">¥{{ formatCurrency(agentFunds.settledProfit) }}</span>
            </div>
            <div class="funds-item balance-item">
              <span class="funds-label">可提现余额</span>
              <span class="funds-value balance">¥{{ formatCurrency(agentFunds.balance) }}</span>
            </div>
            <div class="funds-divider"></div>
            <div class="funds-item bank-info">
              <span class="funds-label">持卡人</span>
              <span class="funds-value">{{ agentFunds.bankUserName || '未设置' }}</span>
            </div>
            <div class="funds-item bank-info">
              <span class="funds-label">开户银行</span>
              <span class="funds-value">{{ agentFunds.bankName || '未设置' }}</span>
            </div>
            <div class="funds-item bank-info">
              <span class="funds-label">银行卡号</span>
              <span class="funds-value">{{ agentFunds.bankCardNumber ? maskBankCard(agentFunds.bankCardNumber) : '未设置' }}</span>
            </div>
            <div class="funds-item bank-info" v-if="agentFunds.bankBranch">
              <span class="funds-label">开户支行</span>
              <span class="funds-value">{{ agentFunds.bankBranch }}</span>
            </div>
            <div class="funds-bank-action">
              <el-button type="text" size="small" @click="openBankCardDialog">
                {{ agentFunds.bankCardNumber ? '修改银行卡' : '绑定银行卡' }}
              </el-button>
            </div>
          </div>
        </div>

        <!-- 业绩排名 -->
        <div class="ranking-section">
          <div class="section-header">
            <h3 class="section-title">业绩排名</h3>
            <span class="ranking-position">第 {{ ranking.position }} 名</span>
          </div>
          <div class="ranking-content">
            <div class="ranking-stats">
              <div class="ranking-stat">
                <div class="stat-label">本月签约</div>
                <div class="stat-value">{{ ranking.monthlyContracts }}</div>
              </div>
              <div class="ranking-stat">
                <div class="stat-label">本月佣金</div>
                <div class="stat-value">¥{{ ranking.monthlyCommission }}</div>
              </div>
              <div class="ranking-stat">
                <div class="stat-label">转化率</div>
                <div class="stat-value">{{ ranking.conversionRate }}%</div>
              </div>
            </div>
            <div class="ranking-progress">
              <div class="progress-label">
                <span>距离上一名</span>
                <span class="progress-value">还差 ¥{{ ranking.gapToPrev }}</span>
              </div>
              <el-progress
                  :percentage="ranking.progress"
                  :stroke-width="8"
                  color="#1890ff"
              ></el-progress>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== 提现对话框 ==================== -->
    <el-dialog
        title="提现申请"
        :visible.sync="withdrawDialog.visible"
        width="460px"
        @close="handleWithdrawDialogClose"
        append-to-body
        :close-on-click-modal="false"
    >
      <div class="withdraw-info">
        <div class="withdraw-balance">
          <span class="label">可提现余额</span>
          <span class="value">¥{{ formatCurrency(agentFunds.balance) }}</span>
        </div>
        <el-form :model="withdrawForm" :rules="withdrawRules" ref="withdrawFormRef" label-width="100px">
          <el-form-item label="提现金额" prop="amount" required>
            <el-input-number
                v-model="withdrawForm.amount"
                :min="1"
                :max="agentFunds.balance / 100"
                :precision="2"
                :step="10"
                controls-position="right"
                style="width: 100%;"
                placeholder="请输入提现金额"
            />
            <div class="withdraw-tips">
              <span>最小提现 1.00 元</span>
              <span class="max-tip">最大 {{ formatCurrency(agentFunds.balance) }}</span>
            </div>
          </el-form-item>

          <el-divider content-position="center">提现账户信息</el-divider>

          <el-form-item label="持卡人姓名" prop="bankUserName" required>
            <el-input v-model="withdrawForm.bankUserName" placeholder="请输入持卡人姓名" />
          </el-form-item>
          <el-form-item label="开户银行" prop="bankName" required>
            <el-select v-model="withdrawForm.bankName" placeholder="请选择开户银行" style="width: 100%;">
              <el-option label="中国工商银行" value="中国工商银行" />
              <el-option label="中国农业银行" value="中国农业银行" />
              <el-option label="中国银行" value="中国银行" />
              <el-option label="中国建设银行" value="中国建设银行" />
              <el-option label="交通银行" value="交通银行" />
              <el-option label="招商银行" value="招商银行" />
              <el-option label="浦发银行" value="浦发银行" />
              <el-option label="兴业银行" value="兴业银行" />
              <el-option label="中信银行" value="中信银行" />
              <el-option label="民生银行" value="民生银行" />
              <el-option label="光大银行" value="光大银行" />
              <el-option label="平安银行" value="平安银行" />
              <el-option label="其他" value="其他" />
            </el-select>
          </el-form-item>
          <el-form-item label="银行卡号" prop="bankCardNumber" required>
            <el-input v-model="withdrawForm.bankCardNumber" placeholder="请输入银行卡号" maxlength="32" />
          </el-form-item>
          <el-form-item label="开户支行" prop="bankBranch">
            <el-input v-model="withdrawForm.bankBranch" placeholder="请输入开户支行（选填）" />
          </el-form-item>
          <el-form-item label="备注" prop="remark">
            <el-input v-model="withdrawForm.remark" type="textarea" :rows="2" placeholder="选填备注信息" maxlength="100" />
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="withdrawDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submitWithdraw" :loading="withdrawDialog.loading">提交申请</el-button>
      </span>
    </el-dialog>

    <!-- ==================== 绑定银行卡对话框 ==================== -->
    <el-dialog
        :title="agentFunds.bankCardNumber ? '修改银行卡' : '绑定银行卡'"
        :visible.sync="bankCardDialog.visible"
        width="420px"
        @close="handleBankCardDialogClose"
        append-to-body
    >
      <el-form :model="bankCardForm" :rules="bankCardRules" ref="bankCardFormRef" label-width="100px">
        <el-form-item label="持卡人姓名" prop="bankUserName" required>
          <el-input v-model="bankCardForm.bankUserName" placeholder="请输入持卡人姓名" />
        </el-form-item>
        <el-form-item label="开户银行" prop="bankName" required>
          <el-select v-model="bankCardForm.bankName" placeholder="请选择开户银行" style="width: 100%;">
            <el-option label="中国工商银行" value="中国工商银行" />
            <el-option label="中国农业银行" value="中国农业银行" />
            <el-option label="中国银行" value="中国银行" />
            <el-option label="中国建设银行" value="中国建设银行" />
            <el-option label="交通银行" value="交通银行" />
            <el-option label="招商银行" value="招商银行" />
            <el-option label="浦发银行" value="浦发银行" />
            <el-option label="兴业银行" value="兴业银行" />
            <el-option label="中信银行" value="中信银行" />
            <el-option label="民生银行" value="民生银行" />
            <el-option label="光大银行" value="光大银行" />
            <el-option label="平安银行" value="平安银行" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="银行卡号" prop="bankCardNumber" required>
          <el-input v-model="bankCardForm.bankCardNumber" placeholder="请输入银行卡号" maxlength="32" />
        </el-form-item>
        <el-form-item label="开户支行" prop="bankBranch">
          <el-input v-model="bankCardForm.bankBranch" placeholder="请输入开户支行（选填）" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="bankCardDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submitBankCard" :loading="bankCardDialog.loading">保存</el-button>
      </span>
    </el-dialog>

    <!-- ==================== 提现结果对话框 ==================== -->
    <el-dialog
        title="提现申请已提交"
        :visible.sync="withdrawResultDialog.visible"
        width="380px"
        append-to-body
        :close-on-click-modal="false"
    >
      <div class="withdraw-result">
        <div class="result-icon success">
          <i class="el-icon-check"></i>
        </div>
        <div class="result-title">申请已提交</div>
        <div class="result-desc">平台审核中，请耐心等待</div>
        <div class="result-detail">
          <div class="detail-item">
            <span class="label">提现金额</span>
            <span class="value">¥{{ formatCurrency(withdrawResultDialog.amount) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">申请时间</span>
            <span class="value">{{ withdrawResultDialog.time }}</span>
          </div>
          <div class="detail-item">
            <span class="label">状态</span>
            <span class="value status-pending">审核中</span>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" @click="withdrawResultDialog.visible = false">知道了</el-button>
      </span>
    </el-dialog>

    <!-- 底部版权信息 -->
    <div class="dashboard-footer">
      <div class="footer-content">
        <span>© 2024 融商加盟平台</span>
        <span>系统版本: v1.0.0</span>
        <span>最后数据更新: {{ lastUpdateTime }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import api from "@/utils/api";

use([
  CanvasRenderer,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);

export default {
  name: 'AgentDashboard',

  components: {
    VChart
  },

  data() {
    return {
      fundsLoading: false,
      agentInfo: {
        name: '测试代理001',
        level: '高级',
        avatar: ''
      },

      // 代理资金数据（从后端接口查询）
      agentFunds: {
        totalProfit: 0,
        settledProfit: 0,
        pendingProfit: 0,
        balance: 0,
        bankCardNumber: '',
        bankName: '',
        bankBranch: '',
        bankUserName: '',
        bankCardUpdateTime: 0
      },

      // 提现对话框
      withdrawDialog: {
        visible: false,
        loading: false
      },

      withdrawForm: {
        amount: null,
        bankUserName: '',
        bankName: '',
        bankCardNumber: '',
        bankBranch: '',
        remark: ''
      },

      withdrawRules: {
        amount: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          { type: 'number', min: 1, message: '提现金额不能小于1元', trigger: 'blur' }
        ],
        bankUserName: [
          { required: true, message: '请输入持卡人姓名', trigger: 'blur' }
        ],
        bankName: [
          { required: true, message: '请选择开户银行', trigger: 'change' }
        ],
        bankCardNumber: [
          { required: true, message: '请输入银行卡号', trigger: 'blur' },
          { pattern: /^\d{16,19}$/, message: '请输入正确的银行卡号（16-19位数字）', trigger: 'blur' }
        ]
      },

      withdrawResultDialog: {
        visible: false,
        amount: 0,
        time: ''
      },

      bankCardDialog: {
        visible: false,
        loading: false
      },

      bankCardForm: {
        bankUserName: '',
        bankName: '',
        bankCardNumber: '',
        bankBranch: ''
      },

      bankCardRules: {
        bankUserName: [
          { required: true, message: '请输入持卡人姓名', trigger: 'blur' }
        ],
        bankName: [
          { required: true, message: '请选择开户银行', trigger: 'change' }
        ],
        bankCardNumber: [
          { required: true, message: '请输入银行卡号', trigger: 'blur' },
          { pattern: /^\d{16,19}$/, message: '请输入正确的银行卡号（16-19位数字）', trigger: 'blur' }
        ]
      },

      statsData: [
        {
          type: 'leads',
          title: '新增线索',
          value: '15',
          icon: 'el-icon-user',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+3'
          }
        },
        {
          type: 'followups',
          title: '今日跟进',
          value: '8',
          icon: 'el-icon-phone-outline',
          trend: {
            type: 'down',
            icon: 'el-icon-bottom',
            value: '-1'
          }
        },
        {
          type: 'contracts',
          title: '本月签约',
          value: '3',
          icon: 'el-icon-document-checked',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+1'
          }
        },
        {
          type: 'commission',
          title: '预估佣金',
          value: '¥85,200',
          icon: 'el-icon-money',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+12.5%'
          }
        },
        {
          type: 'conversion',
          title: '转化率',
          value: '20%',
          icon: 'el-icon-data-line',
          trend: {
            type: 'stable',
            icon: 'el-icon-minus',
            value: '持平'
          }
        }
      ],

      trendPeriod: 'week',
      trendChartData: {
        week: {
          dates: ['1/16', '1/17', '1/18', '1/19', '1/20', '1/21'],
          leads: [5, 3, 2, 4, 2, 3],
          contracts: [1, 4, 3, 3, 1, 4],
          commission: [0, 28000, 15000, 18000, 25000, 0]
        },
        month: {
          dates: ['1月', '2月', '3月', '4月', '5月', '6月'],
          leads: [45, 52, 48, 55, 60, 58],
          contracts: [8, 10, 9, 12, 15, 13],
          commission: [95000, 120000, 105000, 135000, 165000, 145000]
        },
        quarter: {
          dates: ['Q1', 'Q2', 'Q3', 'Q4'],
          leads: [43, 45, 40, 48],
          contracts: [40, 45, 42, 46],
          commission: [320000, 400000, 370000, 430000]
        }
      },

      chartOption: {},

      quickActions: [
        {
          title: '添加线索',
          desc: '录入新客户信息',
          icon: 'el-icon-user-solid',
          color: '#1890ff',
          action: 'addLead'
        },
        {
          title: '电话跟进',
          desc: '联系潜在客户',
          icon: 'el-icon-phone',
          color: '#52c41a',
          action: 'phoneFollow'
        },
        {
          title: '记录跟进',
          desc: '添加跟进记录',
          icon: 'el-icon-edit',
          color: '#fa8c16',
          action: 'recordFollow'
        },
        {
          title: '发起签约',
          desc: '创建签约合同',
          icon: 'el-icon-document-add',
          color: '#722ed1',
          action: 'createContract'
        },
        {
          title: '佣金查询',
          desc: '查看佣金明细',
          icon: 'el-icon-money',
          color: '#f5222d',
          action: 'checkCommission'
        },
        {
          title: '客户关怀',
          desc: '发送关怀消息',
          icon: 'el-icon-chat-line-round',
          color: '#13c2c2',
          action: 'customerCare'
        }
      ],

      todos: [
        {
          id: 1,
          title: '联系李总确认签约意向',
          time: '今天 14:30',
          priority: 'high',
          priorityLabel: '高',
          completed: false
        },
        {
          id: 2,
          title: '提交王先生的资质审核材料',
          time: '今天 16:00',
          priority: 'medium',
          priorityLabel: '中',
          completed: false
        },
        {
          id: 3,
          title: '准备明天展会的宣传资料',
          time: '明天 10:00',
          priority: 'medium',
          priorityLabel: '中',
          completed: false
        },
        {
          id: 4,
          title: '跟进张女士的加盟咨询',
          time: '昨天 17:30',
          priority: 'low',
          priorityLabel: '低',
          completed: false
        },
        {
          id: 5,
          title: '更新本月业绩报告',
          time: '本周五',
          priority: 'low',
          priorityLabel: '低',
          completed: true
        }
      ],

      recentActivities: [
        {
          id: 1,
          type: 'success',
          icon: 'el-icon-circle-check',
          title: '签约成功',
          description: '与「上海餐饮有限公司」成功签约',
          time: '10分钟前'
        },
        {
          id: 2,
          type: 'info',
          icon: 'el-icon-message',
          title: '新线索分配',
          description: '收到新的潜在客户「李先生」',
          time: '30分钟前'
        },
        {
          id: 3,
          type: 'warning',
          icon: 'el-icon-warning-outline',
          title: '待办提醒',
          description: '有2个客户的跟进即将过期',
          time: '1小时前'
        },
        {
          id: 4,
          type: 'money',
          icon: 'el-icon-money',
          title: '佣金到账',
          description: '佣金 ¥25,000 已结算到账',
          time: '2小时前'
        },
        {
          id: 5,
          type: 'share',
          icon: 'el-icon-share',
          title: '分享成功',
          description: '分享的加盟方案被浏览15次',
          time: '3小时前'
        }
      ],

      ranking: {
        position: 3,
        monthlyContracts: 12,
        monthlyCommission: '85,200',
        conversionRate: '20.5',
        gapToPrev: '12,500',
        progress: 68
      },

      currentDate: '',
      currentWeekday: '',
      lastUpdateTime: ''
    };
  },

  computed: {
    currentChartData() {
      return this.trendChartData[this.trendPeriod];
    }
  },

  mounted() {
    this.initCurrentTime();
    this.updateLastUpdateTime();
    this.initChart();
    this.loadFundsInfo();
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    // ==================== 图表相关 ====================
    initChart() {
      const data = this.currentChartData;

      this.chartOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e8e8e8',
          borderWidth: 1,
          textStyle: { color: '#333' },
          axisPointer: {
            type: 'cross',
            label: { backgroundColor: '#6a7985' }
          },
          formatter: (params) => {
            let html = `<div style="padding: 8px 12px;">
              <div style="font-weight: 600; margin-bottom: 8px; color: #333;">${params[0].axisValue}</div>`;

            params.forEach(item => {
              const color = item.color;
              let value = item.value;
              let label = item.seriesName;

              if (label === '预估佣金') {
                value = `¥${value.toLocaleString()}`;
              }

              html += `<div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 2px; background: ${color}; margin-right: 8px;"></span>
                <span style="flex: 1; color: #666;">${label}:</span>
                <span style="font-weight: 600; color: #333;">${value}</span>
              </div>`;
            });

            html += '</div>';
            return html;
          }
        },
        legend: {
          data: ['线索数量', '签约数量', '预估佣金'],
          right: '20',
          top: '10',
          textStyle: { color: '#666', fontSize: 12 },
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 16
        },
        grid: {
          left: '2%',
          right: '3%',
          bottom: '14%',
          top: '18%',
          containLabel: false
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: data.dates,
          axisLine: { lineStyle: { color: '#ddd' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#666',
            fontSize: 12,
            margin: 12,
            interval: 0,
            align: 'center',
            verticalAlign: 'top',
            formatter: (value) => {
              if (value && value.length > 6) {
                return value.substring(0, 6) + '...';
              }
              return value;
            },
            overflow: 'truncate',
            width: 40
          },
          offset: 5
        },
        yAxis: [
          {
            type: 'value',
            name: '数量',
            position: 'left',
            axisLine: { show: true, lineStyle: { color: '#ddd' } },
            axisTick: { show: false },
            axisLabel: { color: '#666', fontSize: 12, margin: 8 },
            splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
          },
          {
            type: 'value',
            name: '佣金',
            position: 'right',
            axisLine: { show: true, lineStyle: { color: '#ddd' } },
            axisTick: { show: false },
            axisLabel: {
              color: '#666',
              fontSize: 12,
              formatter: (value) => {
                if (value >= 10000) {
                  return `¥${(value / 10000).toFixed(0)}万`;
                }
                return `¥${value}`;
              }
            },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: '线索数量',
            type: 'line',
            smooth: true,
            lineStyle: { width: 3, color: '#1890ff' },
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: { color: '#1890ff', borderColor: '#fff', borderWidth: 2 },
            data: data.leads,
            boundaryGap: ['20%', '20%']
          },
          {
            name: '签约数量',
            type: 'line',
            smooth: true,
            lineStyle: { width: 3, color: '#52c41a' },
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: { color: '#52c41a', borderColor: '#fff', borderWidth: 2 },
            data: data.contracts,
            boundaryGap: ['20%', '20%']
          },
          {
            name: '预估佣金',
            type: 'bar',
            yAxisIndex: 1,
            barWidth: '30%',
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: '#ffd666' },
                  { offset: 1, color: '#faad14' }
                ]
              },
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(250, 173, 20, 0.3)'
              }
            },
            data: data.commission
          }
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicOut'
      };
    },

    handleResize() {
      if (this.$refs.chart) {
        this.$refs.chart.resize();
      }
    },

    onChartMouseOver(params) {
      console.log('Chart hover:', params);
    },

    onChartMouseOut() {},

    updateTrendChart() {
      this.initChart();
      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.$refs.chart.resize();
        }
      });
    },

    // ==================== 时间相关 ====================
    initCurrentTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const day = now.getDate();
      this.currentDate = `${year}年${month}月${day}日`;

      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      this.currentWeekday = weekdays[now.getDay()];
    },

    updateLastUpdateTime() {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      this.lastUpdateTime = `${hours}:${minutes}`;
    },

    getCurrentTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // ==================== 资金数据加载 ====================
    async loadFundsInfo() {
      this.fundsLoading = true;
      try {
        const res = await api.agentMerchant_getFundsInfo();
        if (res.data.code === '000' && res.data.data) {
          this.agentFunds = res.data.data;
        }
      } catch (error) {
        console.error('加载资金信息失败:', error);
        this.$message.error('加载资金信息失败');
      } finally {
        this.fundsLoading = false;
      }
    },

    // ==================== 格式化 ====================
    formatCurrency(value) {
      if (!value && value !== 0) return '0.00';
      return (value / 100).toFixed(2);
    },

    maskBankCard(cardNumber) {
      if (!cardNumber) return '';
      if (cardNumber.length <= 8) return cardNumber;
      const start = cardNumber.substring(0, 4);
      const end = cardNumber.substring(cardNumber.length - 4);
      return start + ' **** **** ' + end;
    },

    // ==================== 提现相关 ====================
    openWithdrawDialog() {
      if (this.agentFunds.balance <= 0) {
        this.$message.warning('余额不足，无法提现');
        return;
      }

      this.withdrawForm = {
        amount: null,
        bankUserName: this.agentFunds.bankUserName || '',
        bankName: this.agentFunds.bankName || '',
        bankCardNumber: this.agentFunds.bankCardNumber || '',
        bankBranch: this.agentFunds.bankBranch || '',
        remark: ''
      };

      this.withdrawDialog.visible = true;
      this.$nextTick(() => {
        this.$refs.withdrawFormRef?.clearValidate();
      });
    },

    async submitWithdraw() {
      this.$refs.withdrawFormRef.validate(async (valid) => {
        if (!valid) return;

        const amountCents = Math.round(this.withdrawForm.amount * 100);
        if (amountCents > this.agentFunds.balance) {
          this.$message.error('提现金额超过可用余额');
          return;
        }

        const bankChanged =
            this.withdrawForm.bankUserName !== this.agentFunds.bankUserName ||
            this.withdrawForm.bankName !== this.agentFunds.bankName ||
            this.withdrawForm.bankCardNumber !== this.agentFunds.bankCardNumber ||
            this.withdrawForm.bankBranch !== this.agentFunds.bankBranch;

        this.withdrawDialog.loading = true;

        try {
          // 如果银行卡信息变更，先保存银行卡
          if (bankChanged) {
            const bankRes = await api.agentMerchant_updateBankCard({
              bankUserName: this.withdrawForm.bankUserName,
              bankName: this.withdrawForm.bankName,
              bankCardNumber: this.withdrawForm.bankCardNumber,
              bankBranch: this.withdrawForm.bankBranch || ''
            });
            if (bankRes.data.code !== '000') {
              this.$message.error(bankRes.data.message || '银行卡信息保存失败');
              this.withdrawDialog.loading = false;
              return;
            }
            // 更新本地数据
            this.agentFunds.bankUserName = this.withdrawForm.bankUserName;
            this.agentFunds.bankName = this.withdrawForm.bankName;
            this.agentFunds.bankCardNumber = this.withdrawForm.bankCardNumber;
            this.agentFunds.bankBranch = this.withdrawForm.bankBranch || '';
            this.agentFunds.bankCardUpdateTime = Date.now();
          }

          // TODO: 调用提现接口
          // const res = await api.agentMerchant_withdraw({
          //   amount: amountCents,
          //   bankUserName: this.withdrawForm.bankUserName,
          //   bankName: this.withdrawForm.bankName,
          //   bankCardNumber: this.withdrawForm.bankCardNumber,
          //   bankBranch: this.withdrawForm.bankBranch || '',
          //   remark: this.withdrawForm.remark
          // });

          this.withdrawDialog.visible = false;
          this.agentFunds.balance -= amountCents;
          this.agentFunds.settledProfit += amountCents;

          this.withdrawResultDialog.amount = amountCents;
          this.withdrawResultDialog.time = this.getCurrentTime();
          this.withdrawResultDialog.visible = true;

          // 刷新资金信息
          this.loadFundsInfo();

          this.$message.success('提现申请已提交，等待平台审核');
        } catch (error) {
          console.error('提现失败:', error);
          this.$message.error('操作失败');
        } finally {
          this.withdrawDialog.loading = false;
        }
      });
    },

    handleWithdrawDialogClose() {
      this.$refs.withdrawFormRef?.resetFields();
      this.withdrawDialog.loading = false;
    },

    // ==================== 银行卡相关 ====================
    openBankCardDialog() {
      this.bankCardForm = {
        bankUserName: this.agentFunds.bankUserName || '',
        bankName: this.agentFunds.bankName || '',
        bankCardNumber: this.agentFunds.bankCardNumber || '',
        bankBranch: this.agentFunds.bankBranch || ''
      };
      this.bankCardDialog.visible = true;
      this.$nextTick(() => {
        this.$refs.bankCardFormRef?.clearValidate();
      });
    },

    async submitBankCard() {
      this.$refs.bankCardFormRef.validate(async (valid) => {
        if (!valid) return;

        this.bankCardDialog.loading = true;
        try {
          const res = await api.agentMerchant_updateBankCard({
            bankUserName: this.bankCardForm.bankUserName,
            bankName: this.bankCardForm.bankName,
            bankCardNumber: this.bankCardForm.bankCardNumber,
            bankBranch: this.bankCardForm.bankBranch || ''
          });

          if (res.data.code === '000') {
            this.bankCardDialog.visible = false;

            // 更新本地数据
            this.agentFunds.bankUserName = this.bankCardForm.bankUserName;
            this.agentFunds.bankName = this.bankCardForm.bankName;
            this.agentFunds.bankCardNumber = this.bankCardForm.bankCardNumber;
            this.agentFunds.bankBranch = this.bankCardForm.bankBranch || '';
            this.agentFunds.bankCardUpdateTime = Date.now();

            this.$message.success('银行卡信息保存成功');
            // 刷新资金信息
            this.loadFundsInfo();
          } else {
            this.$message.error(res.data.message || '保存失败');
          }
        } catch (error) {
          console.error('保存银行卡失败:', error);
          this.$message.error('保存失败');
        } finally {
          this.bankCardDialog.loading = false;
        }
      });
    },

    handleBankCardDialogClose() {
      this.$refs.bankCardFormRef?.resetFields();
      this.bankCardDialog.loading = false;
    },

    // ==================== 其他方法 ====================
    refreshData() {
      this.$message({ message: '数据已刷新', type: 'success' });
      this.updateLastUpdateTime();
      this.initChart();
      this.loadFundsInfo();
    },

    quickAddLead() {
      this.$message({ message: '打开添加线索表单', type: 'info' });
    },

    handleStatClick(type) {
      const routes = {
        leads: '/agent/franchisee',
        followups: '/agent/contract',
        contracts: '/agent/contract',
        commission: '/agent/commission',
        conversion: '/agent/performance'
      };
      if (routes[type]) {
        this.$router.push(routes[type]);
      }
    },

    handleQuickAction(action) {
      const actions = {
        addLead: () => this.$message.info('打开添加线索页面'),
        phoneFollow: () => this.$message.info('打开电话跟进功能'),
        recordFollow: () => this.$message.info('打开跟进记录页面'),
        createContract: () => this.$router.push('/agent/contract'),
        checkCommission: () => this.$router.push('/agent/commission'),
        customerCare: () => this.$message.info('打开客户关怀功能')
      };
      if (actions[action]) actions[action]();
    },

    toggleTodo(id) {
      const todo = this.todos.find(t => t.id === id);
      if (todo) {
        this.$message.success(`任务"${todo.title}"已${todo.completed ? '完成' : '标记为未完成'}`);
      }
    },

    handleTodoAction(todo) {
      this.$message.info(`处理任务: ${todo.title}`);
    },

    viewAllTodos() {
      this.$message.info('打开待办事项列表');
    },

    addTodo() {
      this.$message.info('打开添加待办表单');
    },

    refreshActivities() {
      this.$message.success('动态已刷新');
    }
  }
};
</script>

<style lang="scss" scoped>
.agent-dashboard {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border-radius: 12px;
  color: white;

  .header-left {
    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin: 0 0 8px 0;
    }

    .welcome-text {
      font-size: 16px;
      opacity: 0.9;
      margin: 0 0 12px 0;
    }

    .level-badge {
      display: inline-flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 16px;
      padding: 4px 12px;

      .badge {
        background: #ff4d4f;
        color: white;
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 10px;
        margin-right: 6px;
      }

      .level-text {
        font-size: 14px;
      }
    }
  }

  .header-right {
    text-align: right;

    .current-date {
      margin-bottom: 16px;

      .date {
        font-size: 18px;
        font-weight: 600;
        margin-right: 8px;
      }

      .weekday {
        font-size: 14px;
        opacity: 0.8;
      }
    }
  }
}

.stats-cards {
  margin-bottom: 24px;

  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      font-size: 24px;
      color: white;
    }

    &.leads .stat-icon { background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%); }
    &.followups .stat-icon { background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%); }
    &.contracts .stat-icon { background: linear-gradient(135deg, #722ed1 0%, #531dab 100%); }
    &.commission .stat-icon { background: linear-gradient(135deg, #faad14 0%, #d48806 100%); }
    &.conversion .stat-icon { background: linear-gradient(135deg, #13c2c2 0%, #08979c 100%); }

    .stat-content {
      .stat-title {
        font-size: 14px;
        color: #666;
        margin-bottom: 8px;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
      }

      .stat-trend {
        display: flex;
        align-items: center;
        font-size: 12px;

        i { margin-right: 4px; }

        &.up { color: #52c41a; }
        &.down { color: #ff4d4f; }
        &.stable { color: #666; }

        .trend-text {
          color: #999;
          margin-left: 4px;
        }
      }
    }

    .stat-action {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
      font-size: 12px;
      color: #1890ff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        color: #096dd9;
      }
    }
  }
}

.dashboard-content {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}

.content-left {
  .chart-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .todo-activities-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .todo-section,
  .activities-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    height: 100%;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }

  .echarts-container {
    height: 320px;
    position: relative;
    width: 100%;
    overflow: visible;

    .performance-chart {
      width: 100%;
      height: 100%;
      margin: 0 -10px;
      padding: 0 10px;
    }
  }

  .todo-section {
    .section-header {
      margin-bottom: 16px;

      .todo-count {
        font-size: 14px;
        color: #1890ff;
        font-weight: 500;
      }
    }

    .todo-list {
      max-height: 430px;
      overflow-y: auto;
      padding-right: 8px;

      .todo-item {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .todo-checkbox {
          margin-right: 12px;
        }

        .todo-content {
          flex: 1;

          .todo-title {
            font-size: 14px;
            color: #333;
            margin-bottom: 4px;

            &.completed {
              text-decoration: line-through;
              color: #999;
            }
          }

          .todo-meta {
            display: flex;
            align-items: center;
            font-size: 12px;

            .todo-time {
              color: #666;
              margin-right: 8px;
            }

            .todo-priority {
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 11px;

              &.high {
                background: #fff2f0;
                color: #ff4d4f;
                border: 1px solid #ffccc7;
              }

              &.medium {
                background: #fffbe6;
                color: #faad14;
                border: 1px solid #ffe58f;
              }

              &.low {
                background: #f6ffed;
                color: #52c41a;
                border: 1px solid #b7eb8f;
              }
            }
          }
        }

        .todo-actions {
          margin-left: 8px;
        }
      }
    }

    .todo-footer {
      display: flex;
      justify-content: space-between;
      margin-top: 16px;
    }
  }

  .activities-section {
    .activities-list {
      max-height: 430px;
      overflow-y: auto;
      padding-right: 8px;

      .activity-item {
        display: flex;
        align-items: flex-start;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .activity-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-size: 18px;

          &.success {
            background: #f6ffed;
            color: #52c41a;
          }

          &.info {
            background: #e6f7ff;
            color: #1890ff;
          }

          &.warning {
            background: #fffbe6;
            color: #faad14;
          }

          &.money {
            background: #fff0f6;
            color: #eb2f96;
          }

          &.share {
            background: #f9f0ff;
            color: #722ed1;
          }
        }

        .activity-content {
          flex: 1;

          .activity-title {
            font-size: 14px;
            font-weight: 500;
            color: #333;
            margin-bottom: 4px;
          }

          .activity-desc {
            font-size: 12px;
            color: #666;
            margin-bottom: 4px;
          }

          .activity-time {
            font-size: 11px;
            color: #999;
          }
        }
      }
    }
  }
}

.content-right {
  .quick-actions-section,
  .funds-section,
  .ranking-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }

    .ranking-position {
      font-size: 14px;
      color: #1890ff;
      font-weight: 500;
    }
  }

  .quick-actions-section {
    .actions-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;

      @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }
      @media (max-width: 480px) {
        grid-template-columns: 1fr;
      }

      .action-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 16px 8px;
        border: 1px solid #f0f0f0;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s;
        text-align: center;

        &:hover {
          border-color: #1890ff;
          background: #f0f9ff;
        }

        .action-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
          color: white;
          font-size: 20px;
        }

        .action-content {
          flex: 1;

          .action-title {
            font-size: 13px;
            font-weight: 500;
            color: #333;
            margin-bottom: 4px;
          }

          .action-desc {
            font-size: 11px;
            color: #666;
          }
        }
      }
    }
  }

  .funds-section {
    .funds-content {
      .funds-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .funds-label {
          font-size: 14px;
          color: #666;
        }

        .funds-value {
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        &.balance-item {
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid #e8f4f8;

          .funds-label {
            font-size: 15px;
            font-weight: 500;
            color: #333;
          }

          .funds-value.balance {
            font-size: 20px;
            color: #1890ff;
          }
        }

        &.bank-info {
          padding: 6px 0;
          border-bottom: none;

          .funds-label {
            font-size: 13px;
            color: #999;
          }

          .funds-value {
            font-size: 14px;
            font-weight: 400;
            color: #333;
          }
        }
      }

      .funds-divider {
        height: 1px;
        background: #f0f0f0;
        margin: 8px 0;
      }

      .funds-bank-action {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px dashed #e8e8e8;
        text-align: center;

        .el-button--text {
          color: #1890ff;
          font-size: 13px;
        }
      }
    }
  }

  .ranking-section {
    .ranking-content {
      .ranking-stats {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        margin-bottom: 20px;

        .ranking-stat {
          text-align: center;

          .stat-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 4px;
          }

          .stat-value {
            font-size: 20px;
            font-weight: 600;
            color: #333;
          }
        }
      }

      .ranking-progress {
        .progress-label {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 12px;

          .progress-value {
            color: #1890ff;
            font-weight: 500;
          }
        }
      }
    }
  }
}

/* 提现对话框样式 */
.withdraw-info {
  .withdraw-balance {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #f6ffed;
    border-radius: 8px;
    margin-bottom: 20px;

    .label {
      font-size: 14px;
      color: #666;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      color: #1890ff;
    }
  }

  .withdraw-tips {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #999;
    margin-top: 4px;

    .max-tip {
      color: #1890ff;
    }
  }
}

/* 提现结果对话框样式 */
.withdraw-result {
  text-align: center;
  padding: 16px 0;

  .result-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;

    &.success {
      background: #f6ffed;
      border: 2px solid #b7eb8f;

      i {
        font-size: 32px;
        color: #52c41a;
      }
    }
  }

  .result-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .result-desc {
    font-size: 14px;
    color: #faad14;
    margin-bottom: 16px;
  }

  .result-detail {
    background: #fafafa;
    border-radius: 8px;
    padding: 16px;
    text-align: left;

    .detail-item {
      display: flex;
      justify-content: space-between;
      padding: 6px 0;
      font-size: 14px;

      .label {
        color: #666;
      }

      .value {
        font-weight: 500;
        color: #333;

        &.status-pending {
          color: #faad14;
        }
      }
    }
  }
}

.dashboard-footer {
  margin-top: 40px;
  padding: 16px;
  text-align: center;
  color: #666;
  font-size: 12px;
  border-top: 1px solid #f0f0f0;

  .footer-content {
    display: flex;
    justify-content: center;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
    }
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;

    .header-right {
      margin-top: 16px;
      text-align: left;
    }
  }

  .stats-cards .row {
    grid-template-columns: 1fr;
  }

  .dashboard-content {
    gap: 16px;
  }

  .echarts-container {
    height: 280px;

    .performance-chart {
      margin: 0 -5px;
      padding: 0 5px;
    }
  }
}

@media (max-width: 480px) {
  .echarts-container {
    height: 250px;
  }
}
</style>